import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPracticeAreaBySlug, getPracticeAreaByKey } from '../../content/practiceAreas';
import usePageMeta from '../../hooks/usePageMeta';
import useLangPrefix from '../../hooks/useLangPrefix';
import { PHONE_TEL_URL, PHONE_DISPLAY, WHATSAPP_URL, GOOGLE_MAPS_URL } from '../../constants';
import './PracticeAreaDetail.css';

function PracticeAreaDetail() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const langPrefix = useLangPrefix();
  const area = getPracticeAreaBySlug(slug);

  const title = area ? t(`practiceAreas.areas.${area.key}.title`) : '';
  const description = area ? t(`practiceAreas.areas.${area.key}.description`) : '';
  const lang = i18n.language === 'ar' ? 'ar' : i18n.language === 'fr' ? 'fr' : 'en';
  const keywords = area ? area.keywords?.[lang] || area.keywords?.en : undefined;
  const frPath = area ? `/fr${area.path}` : null;

  usePageMeta(
    area
      ? {
          title: `${title} | Marwa Hawshar`,
          description,
          keywords,
          path: `${langPrefix}${area.path}`,
          alternates: [
            { hreflang: 'ar', path: area.path },
            area.enPath ? { hreflang: 'en', path: area.enPath } : null,
            { hreflang: 'fr', path: frPath },
          ].filter(Boolean),
        }
      : {}
  );

  if (!area) {
    return <Navigate to={`${langPrefix}/practice-areas`} replace />;
  }

  const bullets = t(`practiceAreas.areas.${area.key}.detail.bullets`, { returnObjects: true });
  const hasBullets = Array.isArray(bullets) && bullets.length > 0;
  const sharedBody = area.hasSharedBody
    ? t('practiceAreas.sharedBody', { returnObjects: true })
    : null;
  const ownBody = t(`practiceAreas.areas.${area.key}.detail.body`, { returnObjects: true, defaultValue: [] });
  const ctaPrompt = t(`practiceAreas.areas.${area.key}.detail.ctaPrompt`, { defaultValue: '' });
  const relatedAreas = Array.isArray(area.related)
    ? area.related.map((key) => getPracticeAreaByKey(key)).filter(Boolean)
    : [];

  return (
    <article className="py-5 py-lg-6 bg-white">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
              <Link to={`${langPrefix}/practice-areas`} className="link-navy text-decoration-none small d-inline-block">
                {t('practiceAreas.backToList')}
              </Link>
              <div className="d-flex flex-wrap gap-3">
                {i18n.language !== 'fr' && (
                  <Link to={frPath} className="link-navy text-decoration-none small d-inline-block" lang="fr">
                    Lire en français
                  </Link>
                )}
                {area.enPath && i18n.language !== 'en' && (
                  <Link to={area.enPath} className="link-navy text-decoration-none small d-inline-block" lang="en">
                    Read in English
                  </Link>
                )}
                {i18n.language !== 'ar' && (
                  <Link
                    to={area.path}
                    className="link-navy text-decoration-none small d-inline-block"
                    lang="ar"
                    onClick={() => i18n.changeLanguage('ar')}
                  >
                    بالعربية
                  </Link>
                )}
              </div>
            </div>

            <div className="areaIcon mb-3">
              <img src={area.icon} alt="" />
            </div>
            <h1 className="h2 fw-semibold text-navy mb-3">{title}</h1>
            <p className="lead text-muted mb-4">{description}</p>
            <p className="mb-4">{t(`practiceAreas.areas.${area.key}.detail.intro`)}</p>

            {Array.isArray(sharedBody) &&
              sharedBody.map((paragraph, i) => (
                <p className="mb-4" key={`shared-${i}`}>
                  {paragraph}
                </p>
              ))}

            {Array.isArray(ownBody) &&
              ownBody.map((paragraph, i) => (
                <p className="mb-4" key={`own-${i}`}>
                  {paragraph}
                </p>
              ))}

            {hasBullets && (
              <>
                <h2 className="h5 fw-semibold text-navy mb-3">{t('practiceAreas.detailBulletsHeading')}</h2>
                <ul className="mb-4">
                  {bullets.map((bullet, i) => (
                    <li key={i}>{bullet}</li>
                  ))}
                </ul>
              </>
            )}

            <p className="small text-muted mb-4">{t('practiceAreas.disclaimer')}</p>

            {ctaPrompt && <p className="fw-semibold text-navy mb-3">{ctaPrompt}</p>}

            <div className="d-flex flex-wrap gap-3 mb-5">
              <Link to={`${langPrefix}/contact`} className="btn btn-navy">
                {t('nav.contact')}
              </Link>
              <a className="btn btn-outline-navy" href={PHONE_TEL_URL} dir="ltr">
                {t('hero.ctaCall')}
              </a>
              <a
                className="btn btn-success"
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('hero.ctaWhatsapp')}
              </a>
            </div>

            {relatedAreas.length > 0 && (
              <div className="mb-5">
                <h2 className="h6 fw-semibold text-navy mb-3">{t('practiceAreas.relatedHeading')}</h2>
                <div className="d-flex flex-wrap gap-2">
                  {relatedAreas.map((related) => (
                    <Link key={related.key} to={`${langPrefix}${related.path}`} className="btn btn-sm btn-outline-secondary">
                      {t(`practiceAreas.areas.${related.key}.cardTitle`, {
                        defaultValue: t(`practiceAreas.areas.${related.key}.title`),
                      })}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="border-top pt-4 small text-muted">
              <div className="fw-semibold text-navy mb-1">{t('contact.officeLabel')}</div>
              <div>{t('contact.address')}, {t('contact.city')}</div>
              <div className="mt-1">
                <a className="link-navy text-decoration-none" href={PHONE_TEL_URL} dir="ltr">
                  {t('contact.phoneLabel')}: {PHONE_DISPLAY}
                </a>
              </div>
              <a
                className="link-navy text-decoration-none"
                href={GOOGLE_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('contact.openMaps')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

export default PracticeAreaDetail;
